import { useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useApplyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { releaseSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { Product } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useDuplicateProduct } from '../hooks/use-duplicate-product';

const HANDLE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

/** Blank title and handle let the backend pick "<title> (copy)" and a free "-copy" handle. */
const duplicateFormSchema = z.object({
  title: z.string().trim().max(200, { error: 'products:validation.titleLength' }),
  handle: z
    .string()
    .trim()
    .max(120)
    .refine((v) => v === '' || HANDLE.test(v), { error: 'products:validation.handleFormat' }),
  copyMedia: z.boolean(),
  copyVariants: z.boolean(),
});

type DuplicateForm = z.infer<typeof duplicateFormSchema>;

export interface DuplicateProductDialogProps {
  product: Pick<Product, 'id' | 'title' | 'handle'>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function DialogBody({
  product,
  onClose,
}: {
  product: Pick<Product, 'id' | 'title' | 'handle'>;
  onClose: () => void;
}) {
  const { t } = useTranslation(['products', 'common']);
  const message = useErrorMessage();
  const navigate = useNavigate();
  const duplicate = useDuplicateProduct(product.id);
  const form = useForm<DuplicateForm>({
    resolver: useI18nResolver(duplicateFormSchema),
    defaultValues: { title: '', handle: '', copyMedia: true, copyVariants: true },
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      const copy = await duplicate.mutateAsync({
        ...(values.title ? { title: values.title } : {}),
        ...(values.handle ? { handle: values.handle } : {}),
        copyMedia: values.copyMedia,
        copyVariants: values.copyVariants,
      });
      onClose();
      // The copy exists now; a dirty original form must not block the move to it.
      releaseSaveBar();
      await navigate({ to: '/org/products/$productId', params: { productId: copy.id } });
    } catch (error) {
      if (!applyFieldErrors(error)) {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });

  const check = (name: 'copyMedia' | 'copyVariants', label: string, hint: string) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-row items-start gap-2.5">
          <FormControl>
            <Checkbox
              checked={field.value}
              onCheckedChange={(v) => field.onChange(v === true)}
              className="mt-0.5"
            />
          </FormControl>
          <div className="grid gap-0.5">
            <FormLabel className="font-normal">{label}</FormLabel>
            <FormDescription>{hint}</FormDescription>
          </div>
        </FormItem>
      )}
    />
  );

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {form.formState.errors.root?.server?.message ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
          </Alert>
        ) : null}
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('products:fields.title')}</FormLabel>
              <FormControl>
                <Input
                  autoFocus
                  {...field}
                  placeholder={t('products:duplicate.titlePlaceholder', { title: product.title })}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="handle"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('products:fields.handle')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  className="font-mono text-[13px]"
                  placeholder={`${product.handle}-copy`}
                />
              </FormControl>
              <FormDescription>{t('products:duplicate.handleHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        {check(
          'copyMedia',
          t('products:duplicate.copyMedia'),
          t('products:duplicate.copyMediaHint'),
        )}
        {check(
          'copyVariants',
          t('products:duplicate.copyVariants'),
          t('products:duplicate.copyVariantsHint'),
        )}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={duplicate.isPending}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={duplicate.isPending}>
            {duplicate.isPending ? t('products:duplicate.working') : t('products:duplicate.submit')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/**
 * Copies the master product into a draft: attributes, options and customizations always; media
 * and variants when ticked. Variants arrive without SKU or barcode, which are unique.
 */
export function DuplicateProductDialog({
  product,
  open,
  onOpenChange,
}: DuplicateProductDialogProps) {
  const { t } = useTranslation('products');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('duplicate.title')}</DialogTitle>
          <DialogDescription>{t('duplicate.description')}</DialogDescription>
        </DialogHeader>
        {open ? <DialogBody product={product} onClose={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}
